// Shared in-memory state for the map editor. A single observable object
// backs all panes (object tree, inspector, viewport, toolbar) so edits flow
// in one direction and every consumer re-renders from the same source of
// truth.
//
// "Structurally clone on write" keeps undo cheap — each commit pushes a
// snapshot onto the undo stack. We cap undo at 50 entries.
//
// Typed commits: every commit declares whether it's structural (rebuild
// scene), transform-only (just move existing Object3Ds), visual-only (swap
// material on existing meshes), or meta (label/desc/etc — no scene impact).
// `syncedIds` tells the viewport which objects are already up-to-date in the
// scene (e.g. after a gizmo drag), so the apply path becomes a no-op.
//
// Selection lives on its own listener channel — selection changes don't
// invalidate the viewport, only the panes that show the selection.

import type { MapJSON } from '../../maps/MapJSON';

const UNDO_CAP = 50;

export type CommitKind = 'structural' | 'transform' | 'visual' | 'meta';

export interface CommitOpts {
  kind: CommitKind;
  /** ids whose scene-objects already match the new JSON (e.g. after a
   *  gizmo drag). The viewport skips re-applying these. */
  syncedIds?: string[];
}

export interface EditorStateData {
  bundleId: string;
  json: MapJSON;
  /** Multi-select. Empty = nothing selected. Each id is one of:
   *    'map' | 'sky' | 'ibl' | 'picker' | 'audio'
   *    'light:<id>' | 'obj:<id>' | 'behavior:<id>' */
  selectionIds: string[];
  primarySelectionId: string;
  dirty: boolean;
  /** Ephemeral set of object ids whose viewport visibility is overridden
   *  to hidden. Lives outside the JSON; not exported. */
  hiddenIds: ReadonlySet<string>;
}

interface UndoEntry { json: MapJSON; kind: CommitKind }

type DocListener = (opts: CommitOpts) => void;
type SelectionListener = () => void;

export class EditorState {
  private data: EditorStateData;
  private docListeners: DocListener[] = [];
  private selectionListeners: SelectionListener[] = [];
  private undoStack: UndoEntry[] = [];
  private redoStack: UndoEntry[] = [];

  constructor(initial: Omit<EditorStateData, 'selectionIds' | 'primarySelectionId' | 'hiddenIds'> & {
    selectionId?: string;
  }) {
    const sel = initial.selectionId ?? '';
    this.data = {
      bundleId: initial.bundleId,
      json: initial.json,
      dirty: initial.dirty,
      selectionIds: sel ? [sel] : [],
      primarySelectionId: sel,
      hiddenIds: new Set()
    };
  }

  get(): EditorStateData {
    return this.data;
  }

  // ---------- subscription ----------

  subscribeDoc(fn: DocListener): () => void {
    this.docListeners.push(fn);
    return () => {
      const i = this.docListeners.indexOf(fn);
      if (i >= 0) this.docListeners.splice(i, 1);
    };
  }

  subscribeSelection(fn: SelectionListener): () => void {
    this.selectionListeners.push(fn);
    return () => {
      const i = this.selectionListeners.indexOf(fn);
      if (i >= 0) this.selectionListeners.splice(i, 1);
    };
  }

  // ---------- selection ----------

  setSelection(ids: string[], primary?: string): void {
    const next = ids.slice();
    const nextPrimary = primary ?? next[next.length - 1] ?? '';
    if (sameArray(this.data.selectionIds, next) && this.data.primarySelectionId === nextPrimary) return;
    this.data = { ...this.data, selectionIds: next, primarySelectionId: nextPrimary };
    this.notifySelection();
  }

  toggleSelection(id: string): void {
    const cur = this.data.selectionIds;
    const i = cur.indexOf(id);
    const next = i >= 0 ? cur.slice(0, i).concat(cur.slice(i + 1)) : cur.concat(id);
    this.setSelection(next, next[next.length - 1] ?? '');
  }

  clearSelection(): void {
    this.setSelection([], '');
  }

  // ---------- visibility (ephemeral) ----------

  setHidden(id: string, hidden: boolean): void {
    const next = new Set(this.data.hiddenIds);
    if (hidden) next.add(id); else next.delete(id);
    this.data = { ...this.data, hiddenIds: next };
    // Visibility doesn't fire doc listeners (no JSON change); fire selection
    // listeners so the tree row's eye toggle re-renders.
    this.notifySelection();
  }

  // ---------- commits ----------

  commit(nextJson: MapJSON, opts: CommitOpts): void {
    this.undoStack.push({ json: this.data.json, kind: opts.kind });
    while (this.undoStack.length > UNDO_CAP) this.undoStack.shift();
    this.redoStack.length = 0;
    this.data = { ...this.data, json: nextJson, dirty: true };
    this.notifyDoc(opts);
  }

  /** Convenience for transform-only commits — caller mutates a shallow clone
   *  of the json, syncedIds names the objects already updated in the scene. */
  commitTransform(syncedIds: string[], mutate: (j: MapJSON) => MapJSON): void {
    this.commit(mutate(this.data.json), { kind: 'transform', syncedIds });
  }

  loadBundle(bundleId: string, json: MapJSON): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.data = {
      bundleId, json,
      selectionIds: [], primarySelectionId: '',
      dirty: false,
      hiddenIds: new Set()
    };
    // Bundle swap is structural by definition.
    this.notifyDoc({ kind: 'structural' });
    this.notifySelection();
  }

  markClean(): void {
    if (!this.data.dirty) return;
    this.data = { ...this.data, dirty: false };
    this.notifyDoc({ kind: 'meta' });
  }

  undo(): void {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push({ json: this.data.json, kind: prev.kind });
    this.data = { ...this.data, json: prev.json, dirty: true };
    this.notifyDoc({ kind: prev.kind });
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push({ json: this.data.json, kind: next.kind });
    this.data = { ...this.data, json: next.json, dirty: true };
    this.notifyDoc({ kind: next.kind });
  }

  // ---------- internal ----------

  private notifyDoc(opts: CommitOpts): void {
    for (const fn of this.docListeners.slice()) fn(opts);
  }
  private notifySelection(): void {
    for (const fn of this.selectionListeners.slice()) fn();
  }
}

function sameArray(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
